import { useMemo } from 'react'
import cx from 'classnames'
import { Text } from 'blaze-ui/Text/Text'
import { Check2 } from 'blaze-ui/icons'
import { LoadingShimmerer } from 'components/LoadingShimmerer/LoadingShimmerer'
import { CampaignRow, OneOffCampaignRow } from 'components/CampaignRow/CampaignRow'
import { CampaignOption } from 'types'
import Styles from './CampaignDropdown.module.scss'

const FUTURE_STATUSES = new Set(['planned', 'scheduled'])
const isFutureSchedule = (schedule: CampaignOption) =>
  schedule.scheduled_status ? FUTURE_STATUSES.has(schedule.scheduled_status) : false

interface CampaignDropdownProps {
  schedules: CampaignOption[]
  selectedScheduleId: number | undefined
  onSelect: (scheduleId: number) => void
  isLoading?: boolean
  oneOffScheduleId?: number
}

const SkeletonRow = () => (
  <div className={Styles.row} aria-hidden="true">
    <LoadingShimmerer className={Styles.skeletonThumbnail} borderRadius={4} />
    <div className={Styles.skeletonInfo}>
      <LoadingShimmerer className={Styles.skeletonTitle} borderRadius={4} />
      <LoadingShimmerer className={Styles.skeletonMeta} borderRadius={4} />
    </div>
  </div>
)

const SelectedCheckmark = () => (
  <div className={Styles.checkmark} aria-hidden="true">
    <Check2 size={10} color="var(--light-100)" />
  </div>
)

const NoCampaignRow = ({
  isSelected,
  onSelect,
  oneOffScheduleId,
}: {
  isSelected: boolean
  onSelect: (id: number) => void
  oneOffScheduleId: number
}) => (
  <button
    type="button"
    aria-label="One-off content"
    className={cx(Styles.row, { [Styles.selected]: isSelected })}
    onClick={() => onSelect(oneOffScheduleId)}
    aria-current={isSelected ? 'true' : undefined}
  >
    <OneOffCampaignRow />
    {isSelected && <SelectedCheckmark />}
  </button>
)

const ScheduleButton = ({
  schedule,
  isSelected,
  onSelect,
}: {
  schedule: CampaignOption
  isSelected: boolean
  onSelect: (id: number) => void
}) => (
  <button
    type="button"
    className={cx(Styles.row, { [Styles.selected]: isSelected })}
    onClick={() => onSelect(schedule.id)}
    aria-current={isSelected ? 'true' : undefined}
  >
    <CampaignRow schedule={schedule} isChecked={isSelected} />
    {isSelected && <SelectedCheckmark />}
  </button>
)

const ScheduleSection = ({
  label,
  schedules,
  selectedScheduleId,
  onSelect,
}: {
  label: string
  schedules: CampaignOption[]
  selectedScheduleId: number | undefined
  onSelect: (id: number) => void
}) => (
  <div className={Styles.section}>
    <div className={Styles.sectionLabel}>
      <Text variant="secondary" color="var(--dark-40)">
        {label}
      </Text>
    </div>
    <div className={Styles.list}>
      {schedules.map((schedule) => (
        <ScheduleButton
          key={schedule.id}
          schedule={schedule}
          isSelected={schedule.id === selectedScheduleId}
          onSelect={onSelect}
        />
      ))}
    </div>
  </div>
)

export const CampaignDropdown = ({
  schedules,
  selectedScheduleId,
  onSelect,
  isLoading,
  oneOffScheduleId,
}: CampaignDropdownProps) => {
  const showSkeletons = isLoading && schedules.length === 0

  const [active, future] = useMemo(() => {
    const a: CampaignOption[] = []
    const f: CampaignOption[] = []
    schedules.forEach((s) => (isFutureSchedule(s) ? f.push(s) : a.push(s)))
    return [a, f]
  }, [schedules])

  const sections: { label: string; schedules: CampaignOption[] }[] = [
    { label: 'Active campaigns', schedules: active },
    { label: 'Future campaigns', schedules: future },
  ].filter((s) => s.schedules.length > 0)

  const hasOneOff = oneOffScheduleId !== undefined

  return (
    <div className={Styles.container}>
      {oneOffScheduleId !== undefined && (
        <div className={Styles.section}>
          <NoCampaignRow
            isSelected={selectedScheduleId === oneOffScheduleId}
            onSelect={onSelect}
            oneOffScheduleId={oneOffScheduleId}
          />
        </div>
      )}
      {showSkeletons && (
        <>
          {hasOneOff && <div className={Styles.divider} />}
          <div className={Styles.section}>
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </div>
        </>
      )}
      {!showSkeletons &&
        sections.map((section, idx) => (
          <div key={section.label}>
            {(hasOneOff || idx > 0) && <div className={Styles.divider} />}
            <ScheduleSection
              label={section.label}
              schedules={section.schedules}
              selectedScheduleId={selectedScheduleId}
              onSelect={onSelect}
            />
          </div>
        ))}
    </div>
  )
}
